/**
 * isChosenPaymentMethodAvailable - проверяет, остался ли доступным выбранный метод оплаты, после возможных изменений пользователя в заказе
 * isCashOnReturnMethodAvailable - доступен ли способ оплаты Возврат за минусом доставки
 * returnPaymentMethod - возвращаем в watcher availablePaymentMethods какой метод поставить - либо тот, что изначально запомнили в initialPaymentMethod (для редактирования заказа), либо первый из списка (для создания заказа)
 * watcher availablePaymentMethods - выставляет методы, оперируя условиями из 3 выше описанных computed свойств
 * описание логики работы - https://tracker.yandex.ru/FRONTEND-616
 * */
import { mapState, mapGetters, mapMutations, mapActions } from 'vuex'
import validationMixinGeneral from '@apps/order-form/src/mixins/validationMixinGeneral'
import { required } from '@vuelidate/validators'
import { GENERAL_STORE } from '../store'

// {
//   "deliveryPaymentMethods": [
//   {
//     "name": "Отправитель наличными",
//     "value": 0,
//     "isActive": true,
//     "prompt": null
//   },
//   {
//     "name": "Получатель наличными",
//     "value": 1,
//     "isActive": true,
//     "prompt": null
//   },
//   {
//     "name": "Онлайн банковской картой",
//     "value": 5,
//     "isActive": true,
//     "prompt": "Предоплата по заказу. В случае отмены деньги вернутся на карту."
//   },
//   {
//     "name": "Оплата бонусами",
//     "value": 3,
//     "isActive": true,
//     "prompt": null
//   },
//   {
//     "name": "Оплата с баланса личного кабинета",
//     "value": 2,
//     "isActive": true,
//     "prompt": null
//   }
// ]
// }
const getPaymentSchemeItem = (sort = [], defaultValue = null) => ({ sort, defaultValue })

export const PAYMENT_METHODS_SCHEMA = {
  user: {
    true: {
      isLegalEntity: {
        true: {
          buyout: {
            true: getPaymentSchemeItem([2], 2),
            false: getPaymentSchemeItem([2], 2),
          },
        },
        false: {
          buyout: {
            true: getPaymentSchemeItem(undefined, 5),
            false: getPaymentSchemeItem([5], 5),
          },
        },
      },
    },
    false: {
      buyout: {
        true: getPaymentSchemeItem(undefined, 5),
        false: getPaymentSchemeItem([5], 5),
      },
    },
  },
}

export default {
  mixins: [validationMixinGeneral],

  validations() {
    return {
      chosenPaymentMethod: {
        required,
      },
    }
  },

  data() {
    return {
      initialPaymentMethod: null,
      targetScheme: null,
    }
  },
  created() {
    this.setPaymentScheme()

    // выставляем дефолтный метод оплаты, взависимости от того, создание это или редактирование
    if (this.form?.deliveryObject) {
      const choosedOrderPaymentMethod = this.form.deliveryObject.info.typePay

      this.initialPaymentMethod = choosedOrderPaymentMethod

      this.setPaymentMethodWithCheck(choosedOrderPaymentMethod)
    } else if (this.targetScheme) {
      this.setPaymentMethodWithCheck(this.targetScheme.defaultValue)
    } else {
      this.setPaymentMethod(null)
    }
  },
  mounted() {
    this.addFieldValidator('chosenPaymentMethod', this.$refs.chosenPaymentMethodField)

    this.$store.subscribe(mutation => {
      if (mutation.type === 'removeDot') {
        // Будем менять медот только если выбрана оплата "на точке", и эта точка становится недоступной в следствии удаления
        if (this.chosenPaymentMethod > 100) this.setPaymentMethod(this.returnPaymentMethod)
      }
    })
  },
  computed: {
    ...mapState(GENERAL_STORE, ['base', 'order', 'form']),
    ...mapGetters(GENERAL_STORE, ['isCrmCourierChosen', 'isBuyout']),

    formattedPaymentMethods() {
      if (!this.targetScheme) return this.form.deliveryPaymentMethods

      const methodsList = [...this.form.deliveryPaymentMethods]

      this.targetScheme.sort.forEach(paymentMethodValue => {
        const targetPaymentMethodIdx = methodsList.findIndex(
          method => method.value === paymentMethodValue
        )

        if (!targetPaymentMethodIdx) return

        // Добавляем метод в начало списка
        methodsList.unshift(methodsList[targetPaymentMethodIdx])
        // Удаляем его же со старого места
        methodsList.splice(targetPaymentMethodIdx + 1, 1)
      })

      return methodsList
    },
    availablePaymentMethods() {
      // оставляем только доступные
      return this.formattedPaymentMethods.filter(m => m.isActive)
    },

    chosenPaymentMethod: {
      get() {
        return this.order.paymentMethod
      },
      set(value) {
        this.setPaymentMethod(value)
        this.calculatePrice()
      },
    },
    isChosenPaymentMethodAvailable() {
      const even = method => Object.values(method).includes(this.chosenPaymentMethod)

      return this.availablePaymentMethods.some(even)
    },
    isCashOnReturnMethodAvailable() {
      const even = method => Object.values(method).includes(4)

      return this.availablePaymentMethods.some(even)
    },
    returnPaymentMethod() {
      const even = method => Object.values(method).includes(this.initialPaymentMethod)

      return this.availablePaymentMethods.some(even)
        ? this.initialPaymentMethod
        : this.availablePaymentMethods[0].value
    },
  },
  methods: {
    ...mapMutations(GENERAL_STORE, ['setPaymentMethod', 'addValidator']),
    ...mapActions(GENERAL_STORE, ['calculatePrice']),

    setPaymentScheme() {
      const { user } = this.$store.state[`${GENERAL_STORE}`]
      const isAuth = Boolean(user && Object.keys(user).length)
      const schemePath = PAYMENT_METHODS_SCHEMA.user[isAuth]

      this.targetScheme = isAuth
        ? schemePath.isLegalEntity[user.isLegalEntity].buyout[this.isBuyout]
        : schemePath.buyout[this.isBuyout]
    },
    isCrmCourierHandler() {
      /*
      Если выбран срм курьер - оплата с личного счёта (стоимость всегда 0)
      В противном случае, может остаться банковская карта
      если она не привязана - запросит привязку
      */
      if (this.isCrmCourierChosen) this.setPaymentMethod(2) // (2 - баланс лк)
      else this.setPaymentMethod(null)
    },
    setPaymentMethodWithCheck(paymentMethod = null) {
      // ожидает номер способа оплаты
      const isActualPaymentMethod = this.availablePaymentMethods.find(
        method => method.value.toString() === paymentMethod?.toString()
      )

      this.setPaymentMethod(isActualPaymentMethod ? paymentMethod : null) // Если выбранный способ оплаты актуален - установится,  в противном случае - null
    },
  },
  watch: {
    availablePaymentMethods() {
      // Следим за актуальными способами оплаты, устанавливаем заново выбранный
      this.setPaymentMethodWithCheck(this.chosenPaymentMethod)
    },
    isCrmCourierChosen() {
      this.isCrmCourierHandler()
    },
  },
  beforeDestroy() {
    this.isCrmCourierHandler() // При уничтожении компонента watch не отрабатывает
  },
}
